import type {
  OfferCampaignAdminSummary,
  OfferCampaignAuditQuery,
  OfferCampaignAuditRow,
  OfferCampaignEnrollmentSummary,
  OfferCampaignOverridePayload,
} from '../types/offer-campaigns';
import { OfferCampaignDateInput, formatOfferCampaignDateTime } from './OfferCampaignDateField';

export function OfferCampaignAuditPanel({
  campaigns,
  selectedCampaignId,
  filters,
  auditRows,
  onCampaignChange,
  onFiltersChange,
  onRefresh,
  onExport,
  onSelectAudit,
}: {
  campaigns: OfferCampaignAdminSummary[];
  selectedCampaignId: string;
  filters: OfferCampaignAuditQuery;
  auditRows: OfferCampaignAuditRow[];
  onCampaignChange: (value: string) => void;
  onFiltersChange: (value: OfferCampaignAuditQuery) => void;
  onRefresh: () => void;
  onExport: () => void;
  onSelectAudit: (row: OfferCampaignAuditRow) => void;
}) {
  const setFilter = (key: keyof OfferCampaignAuditQuery, value: string) => onFiltersChange({ ...filters, [key]: value || undefined });
  const campaignLabels = campaignLabelMap(campaigns);
  return (
    <div className="offer-audit-card general-settings-offer-audit-panel">
      <div className="enterprise-card-heading">
        <div>
          <strong>Campaign Audit</strong>
          <span>Review campaign events and troubleshoot offer workflow activity</span>
        </div>
        <div className="row-actions">
          <button className="mini-btn" onClick={onRefresh}>Refresh Audit</button>
          <button className="mini-btn" onClick={onExport} disabled={!auditRows.length}>Export CSV</button>
        </div>
      </div>
      <div className="offer-audit-filters">
        <SelectField label="Campaign" value={selectedCampaignId} options={campaigns.map((row) => row.id)} optionLabels={campaignLabels} onChange={onCampaignChange} />
        <TextField label="MRN" value={filters.mrn || ''} onChange={(value) => setFilter('mrn', value.toUpperCase())} />
        <TextField label="Offer Code" value={filters.offerCode || ''} onChange={(value) => setFilter('offerCode', value.toUpperCase())} />
        <TextField label="Event Type" value={filters.eventType || ''} onChange={(value) => setFilter('eventType', value.toUpperCase())} />
        <TextField label="From" type="datetime-local" value={filters.from || ''} onChange={(value) => setFilter('from', value)} />
        <TextField label="To" type="datetime-local" value={filters.to || ''} onChange={(value) => setFilter('to', value)} />
      </div>
      <div className="table-scroll">
        <table className="enterprise-table offer-audit-table">
          <thead>
            <tr>
              <th>When</th><th>Event Type</th><th>Campaign</th><th>MRN</th><th>Offer Code</th><th>Actor</th><th>Status</th><th>Details</th>
            </tr>
          </thead>
          <tbody>
            {auditRows.map((row) => (
              <tr key={row.id}>
                <td>{formatOfferDateTime(row.createdAt)}</td>
                <td>{row.eventType}</td>
                <td>{campaignLabels[row.campaignId || ''] || '-'}</td>
                <td>{row.mrn || '-'}</td>
                <td>{row.offerCode || '-'}</td>
                <td>{row.actorUsername || 'system'}</td>
                <td><span className={`status-pill ${statusFromAudit(row).toLowerCase()}`}>{statusFromAudit(row)}</span></td>
                <td><button className="mini-btn" onClick={() => onSelectAudit(row)}>Details</button></td>
              </tr>
            ))}
            {!auditRows.length && <tr><td colSpan={8} className="empty-cell">No audit events found for current filters.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function OfferCampaignEnrollmentDrawerPanel({
  campaigns,
  selectedCampaignId,
  filters,
  enrollmentRows,
  canOverride,
  onCampaignChange,
  onFiltersChange,
  onRefresh,
  onExport,
  onSelectEnrollment,
  onOverride,
}: {
  campaigns: OfferCampaignAdminSummary[];
  selectedCampaignId: string;
  filters: OfferCampaignAuditQuery;
  enrollmentRows: OfferCampaignEnrollmentSummary[];
  canOverride: boolean;
  onCampaignChange: (value: string) => void;
  onFiltersChange: (value: OfferCampaignAuditQuery) => void;
  onRefresh: () => void;
  onExport: () => void;
  onSelectEnrollment: (row: OfferCampaignEnrollmentSummary) => void;
  onOverride: (row: OfferCampaignEnrollmentSummary) => void;
}) {
  const setFilter = (key: keyof OfferCampaignAuditQuery, value: string) => onFiltersChange({ ...filters, [key]: value || undefined });
  const campaignLabels = campaignLabelMap(campaigns);
  return (
    <div className="offer-audit-card general-settings-offer-audit-panel">
      <div className="enterprise-card-heading">
        <div>
          <strong>Enrollment Drawer</strong>
          <span>{enrollmentRows.length} latest enrollments match selected filters</span>
        </div>
        <div className="row-actions">
          <button className="mini-btn" onClick={onRefresh}>Refresh Audit</button>
          <button className="mini-btn" onClick={onExport} disabled={!enrollmentRows.length}>Export CSV</button>
        </div>
      </div>
      <div className="offer-audit-filters">
        <SelectField label="Campaign" value={selectedCampaignId} options={campaigns.map((row) => row.id)} optionLabels={campaignLabels} onChange={onCampaignChange} />
        <TextField label="MRN" value={filters.mrn || ''} onChange={(value) => setFilter('mrn', value.toUpperCase())} />
        <TextField label="Offer Code" value={filters.offerCode || ''} onChange={(value) => setFilter('offerCode', value.toUpperCase())} />
      </div>
      <div className="table-scroll">
        <table className="enterprise-table offer-enrollment-table">
          <thead>
            <tr>
              <th>MRN</th><th>Patient</th><th>Center</th><th>Offer Code</th><th>Status</th><th>Created By</th><th>Updated</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {enrollmentRows.map((row) => (
              <tr key={row.id}>
                <td>{row.mrn}</td>
                <td>{row.patientName || '-'}</td>
                <td>{row.centerName || '-'}</td>
                <td>{row.offerCode}</td>
                <td><span className={`status-pill ${row.status.toLowerCase()}`}>{row.status}</span></td>
                <td>{row.createdUsername || '-'}</td>
                <td>{formatOfferDateTime(row.updatedAt)}</td>
                <td>
                  <div className="row-actions">
                    <button className="mini-btn" onClick={() => onSelectEnrollment(row)}>Open Drawer</button>
                    <button className="mini-btn danger" disabled={!canOverride} onClick={() => onOverride(row)}>Admin Override</button>
                  </div>
                </td>
              </tr>
            ))}
            {!enrollmentRows.length && <tr><td colSpan={8} className="empty-cell">No enrollments found for current filters.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function OfferCampaignAuditDetailDrawer({ row, onClose }: { row: OfferCampaignAuditRow; onClose: () => void }) {
  const before = auditState(row.beforeState);
  const after = auditState(row.afterState);
  return (
    <div className="enterprise-drawer-overlay" onClick={onClose}>
      <aside className="enterprise-drawer offer-audit-drawer" onClick={(event) => event.stopPropagation()}>
        <button className="drawer-close" onClick={onClose}>&times;</button>
        <h3>Offer lifecycle audit</h3>
        <p className="offer-audit-lifecycle-intro">{auditEventNarrative(row)}</p>
        <dl className="offer-audit-detail-list">
          <div><dt>Date and time</dt><dd>{formatOfferDateTime(row.createdAt)}</dd></div>
          <div><dt>Workflow event</dt><dd>{friendlyAuditEvent(row.eventType)}</dd></div>
          <div><dt>MRN</dt><dd>{row.mrn || '-'}</dd></div>
          <div><dt>Offer Code</dt><dd>{row.offerCode || '-'}</dd></div>
          <div><dt>Performed by</dt><dd>{row.actorUsername || 'system'}</dd></div>
          {before.status && <div><dt>Status before this event</dt><dd>{before.status}</dd></div>}
          {after.status && <div><dt>Status after this event</dt><dd>{after.status}</dd></div>}
          {after.cancelledAt && <div><dt>Cancellation recorded at</dt><dd>{formatOfferDateTime(after.cancelledAt)}</dd></div>}
          {after.cancelledReason && <div><dt>Cancellation reason</dt><dd>{after.cancelledReason}</dd></div>}
        </dl>
        <details className="offer-audit-technical-detail"><summary>Technical event data</summary><pre className="offer-admin-preview">{JSON.stringify({ beforeState: row.beforeState, afterState: row.afterState, metadata: row.metadata }, null, 2)}</pre></details>
      </aside>
    </div>
  );
}

function auditState(value: unknown): Record<string, string> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.fromEntries(Object.entries(value as Record<string, unknown>).filter(([, entry]) => typeof entry === 'string' || typeof entry === 'number').map(([key, entry]) => [key, String(entry)]));
}

function friendlyAuditEvent(eventType: string): string {
  return eventType.split('_').map((word) => word.charAt(0) + word.slice(1).toLowerCase()).join(' ');
}

function auditEventNarrative(row: OfferCampaignAuditRow): string {
  const actor = row.actorUsername || 'System';
  const descriptions: Record<string, string> = {
    MRN_LOOKUP_STARTED: `${actor} started the MRN and eligibility check.`,
    MRN_LOOKUP_SUCCEEDED: `${actor} completed the MRN lookup.`,
    OFFER_CODE_GENERATED: `${actor} generated an offer code and started the reservation.`,
    ADVANCE_VERIFICATION_STARTED: `${actor} started advance-payment verification.`,
    ADVANCE_VERIFICATION_SUCCEEDED: `${actor} completed advance-payment verification.`,
    ENROLLMENT_COMPLETED: `${actor} completed the enrollment.`,
    ENROLLMENT_CANCELLED: `${actor} cancelled the enrollment before completion.`,
  };
  return descriptions[row.eventType] || `${actor} recorded this workflow event.`;
}

export function OfferCampaignEnrollmentDetailDrawer({
  row,
  canOverride,
  onClose,
  onOverride,
}: {
  row: OfferCampaignEnrollmentSummary;
  canOverride: boolean;
  onClose: () => void;
  onOverride: (row: OfferCampaignEnrollmentSummary) => void;
}) {
  return (
    <div className="enterprise-drawer-overlay" onClick={onClose}>
      <aside className="enterprise-drawer offer-audit-drawer" onClick={(event) => event.stopPropagation()}>
        <button className="drawer-close" onClick={onClose}>&times;</button>
        <h3>Enrollment Audit Drawer</h3>
        <dl className="offer-audit-detail-list">
          <div><dt>MRN</dt><dd>{row.mrn}</dd></div>
          <div><dt>Patient</dt><dd>{row.patientName || '-'}</dd></div>
          <div><dt>Center</dt><dd>{row.centerName || '-'}</dd></div>
          <div><dt>Offer Code</dt><dd>{row.offerCode}</dd></div>
          <div><dt>Status</dt><dd>{row.status}</dd></div>
          <div><dt>Reserved</dt><dd>{formatOfferDateTime(row.reservedAt)}</dd></div>
          <div><dt>Verified</dt><dd>{formatOfferDateTime(row.verifiedAt)}</dd></div>
          <div><dt>Completed</dt><dd>{formatOfferDateTime(row.completedAt)}</dd></div>
          <div><dt>Cancelled</dt><dd>{formatOfferDateTime(row.cancelledAt)}</dd></div>
          <div><dt>Failure</dt><dd>{row.lastVerificationError || row.cancelledReason || '-'}</dd></div>
        </dl>
        <div className="row-actions">
          <button className="mini-btn danger" disabled={!canOverride} onClick={() => onOverride(row)}>Admin Override Enrollment</button>
        </div>
      </aside>
    </div>
  );
}

export function OfferCampaignOverrideEnrollmentModal({
  draft,
  busy,
  onChange,
  onClose,
  onSubmit,
}: {
  draft: OfferCampaignOverridePayload;
  busy: boolean;
  onChange: (draft: OfferCampaignOverridePayload) => void;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const valid = draft.reason.trim().length >= 5 && draft.confirm;
  return (
    <div className="modal-overlay offer-admin-modal-overlay">
      <div className="admin-modal offer-override-modal" role="dialog" aria-modal="true">
        <button onClick={onClose} className="admin-modal-close">&times;</button>
        <h3>Admin Override Enrollment</h3>
        <p className="offer-override-warning">Superadmin-only correction. Every override writes an audit event.</p>
        <div className="offer-config-grid">
          <TextField label="MRN" value={draft.mrn} onChange={(value) => onChange({ ...draft, mrn: value.toUpperCase() })} />
          <TextField label="Offer Code" value={draft.offerCode} onChange={(value) => onChange({ ...draft, offerCode: value.toUpperCase() })} />
          <SelectField
            label="Override Action"
            value={draft.overrideAction}
            options={['MARK_VERIFIED', 'CANCEL_ENROLLMENT', 'EXPIRE_RESERVATION', 'REOPEN_ADVANCE_PENDING']}
            optionLabels={{
              MARK_VERIFIED: 'Mark verified',
              CANCEL_ENROLLMENT: 'Cancel enrollment',
              EXPIRE_RESERVATION: 'Expire reservation',
              REOPEN_ADVANCE_PENDING: 'Reopen advance pending',
            }}
            onChange={(value) => onChange({ ...draft, overrideAction: value as OfferCampaignOverridePayload['overrideAction'] })}
          />
          <label className="offer-config-field offer-config-textarea">
            <span>Reason</span>
            <textarea value={draft.reason} onChange={(event) => onChange({ ...draft, reason: event.target.value })} placeholder="Explain why this manual correction is required." />
          </label>
          <label className="offer-config-toggle">
            <input type="checkbox" checked={draft.confirm} onChange={(event) => onChange({ ...draft, confirm: event.target.checked })} />
            <span>I confirm this override is intentional and audited.</span>
          </label>
        </div>
        <div className="row-actions">
          <button className="btn btn-primary" disabled={!valid || busy} onClick={onSubmit}>{busy ? 'Applying...' : actionLabel(draft.overrideAction)}</button>
          <button className="btn btn-outline" disabled={busy} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export function exportOfferCampaignAuditCsv(rows: OfferCampaignAuditRow[]) {
  const headers = ['when', 'event type', 'campaign', 'MRN', 'offer code', 'actor', 'status', 'details'];
  const body = rows.map((row) => [
    formatOfferDateTime(row.createdAt),
    row.eventType,
    row.campaignId || '',
    row.mrn || '',
    row.offerCode || '',
    row.actorUsername || 'system',
    statusFromAudit(row),
    JSON.stringify({ beforeState: row.beforeState, afterState: row.afterState, metadata: row.metadata }),
  ]);
  downloadOfferCsv(`offer-campaign-audit-${new Date().toISOString().slice(0, 10)}.csv`, [headers, ...body]);
}

export function exportOfferCampaignEnrollmentCsv(rows: OfferCampaignEnrollmentSummary[], filename = `offer-campaign-enrollments-${new Date().toISOString().slice(0, 10)}.csv`) {
  const headers = ['center', 'MRN', 'patient', 'offer code', 'status', 'created by', 'enrollment date', 'plan selected'];
  const body = rows.map((row) => [
    row.centerName || '',
    row.mrn,
    row.patientName || '',
    row.offerCode || '',
    row.status || '',
    row.createdUsername || '',
    formatOfferDateTime(enrollmentDate(row)),
    planSelected(row),
  ]);
  downloadOfferCsv(filename, [headers, ...body]);
}

export function formatOfferDateTime(value: string | null | undefined) {
  return formatOfferCampaignDateTime(value);
}

export function enrollmentDate(row: OfferCampaignEnrollmentSummary): string | null {
  return row.completedAt || row.verifiedAt || row.reservedAt || row.createdAt || row.updatedAt || null;
}

export function planSelected(row: OfferCampaignEnrollmentSummary): string {
  const plan = String(row.planSelected || '').trim();
  if (plan) return plan;
  if (['RESERVED', 'ADVANCE_PENDING', 'FAILED_VERIFICATION'].includes(row.status)) return 'Pending verification';
  if (['CANCELLED', 'EXPIRED'].includes(row.status)) return 'Not selected';
  if (row.status === 'COMPLETED') return 'Not recorded';
  return '-';
}

function statusFromAudit(row: OfferCampaignAuditRow): string {
  const after = row.afterState || {};
  const metadata = row.metadata || {};
  const status = after.status || metadata.status;
  if (status) return String(status);
  return row.eventType.includes('FAILED') ? 'FAILED' : 'OK';
}

function actionLabel(action: OfferCampaignOverridePayload['overrideAction']): string {
  if (action === 'MARK_VERIFIED') return 'Mark verified';
  if (action === 'CANCEL_ENROLLMENT') return 'Cancel enrollment';
  if (action === 'EXPIRE_RESERVATION') return 'Expire reservation';
  return 'Reopen advance pending';
}

function campaignLabelMap(campaigns: OfferCampaignAdminSummary[]): Record<string, string> {
  return campaigns.reduce<Record<string, string>>((acc, row) => ({ ...acc, [row.id]: row.campaignName }), {});
}

function TextField({ label, value, onChange, type = 'text' }: { label: string; value: string; onChange: (value: string) => void; type?: string }) {
  return <label className="offer-config-field"><span>{label}</span>{type === 'datetime-local'
    ? <OfferCampaignDateInput kind="datetime-local" value={value} onChange={onChange} />
    : <input type={type} value={value} onChange={(event) => onChange(event.target.value)} />}{type === 'datetime-local' && <small>Format: DD/MM/YYYY hh:mm AM/PM</small>}</label>;
}

function SelectField({ label, value, options, optionLabels, onChange }: { label: string; value: string; options: string[]; optionLabels?: Record<string, string>; onChange: (value: string) => void }) {
  return <label className="offer-config-field"><span>{label}</span><select value={value} onChange={(event) => onChange(event.target.value)}>{options.map((option) => <option key={option} value={option}>{optionLabels?.[option] || option || 'Select'}</option>)}</select></label>;
}

function downloadOfferCsv(filename: string, rows: unknown[][]) {
  const csv = rows.map((line) => line.map(csvCell).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function csvCell(value: unknown): string {
  const text = String(value ?? '');
  return `"${text.replace(/"/g, '""')}"`;
}
